import classnames from 'classnames';
import {
  ARTICLE_TYPES,
  Badge,
  CardIcon,
  Duration,
  Heading,
  ImageCount,
  Paragraph,
  SponsorCard,
} from '@stadionhq-v3/base-ui';
import { Button } from '@components/v3/atoms/button';
import { Date } from '@components/v3/atoms/date';
import { CategoryLink } from '@components/v3/atoms/category-link';
import styles from './articleCard.module.scss';
import { Author } from '../author';
import { ArticleCardProps } from './articleCard.types';

const ArticleCard = ({
  nativeProps,
  classNames,
  title,
  slug,
  type,
  publishDate,
  image,
  category,
  sponsor,
  featuredLabel,
  isFeatured = false,
  imageCount,
  duration,
  author,
  cardIcon,
  episodes,
  episodesLabel,
  summary,
  ageLimit,
  ageLimitLabel,
  cta,
  membersBadge,
  onDark = false,
  variant = 'default',
  size = 'default',
  cardIconClassNames,
  titleClassNames,
  durationClassNames,
  number,
  isVideoCard,
}: ArticleCardProps) => (
  <article
    {...nativeProps}
    className={classnames(
      [
        styles.articleCard,
        styles[variant],
        styles[size],
        type ? styles[type.toLowerCase()] : null,
        styles.tokens,
        isVideoCard && styles.playlistCard,
        onDark && styles.dark,
      ],
      classNames,
      {
        [classnames(styles.isFeatured)]: isFeatured,
      }
    )}
  >
    {slug && (
      <a href={slug} className={styles.floatingLink} aria-label="Read more">
        <span>{title}</span>
      </a>
    )}

    <div className={styles.container}>
      {membersBadge && (
        <Badge classNames={[classnames(styles.badge)]} {...membersBadge} />
      )}
      <div className={styles.articleDetails}>
        {variant === 'default' && isFeatured && featuredLabel && (
          <div className={classnames(styles.topContainer)}>
            <div className={styles.isFeaturedContainer}>
              <span className={classnames(styles.featuredText)}>
                {featuredLabel}
              </span>
            </div>
            {/* TODO: Add badge in V3 */}
            {/* <Badge
              classNames={[classnames(styles.badge)]}
              text="MEMBERS"
              leftElement={<Lock />}
              /> */}
          </div>
        )}

        {variant === 'default' &&
          type?.toLowerCase() === 'podcast' &&
          author && (
            <Author
              {...author}
              classNames={[classnames(styles.author)]}
              onDark={onDark}
            />
          )}

        <div className={styles.textPanel}>
          <div className={styles.categoryAndPublishDateContainer}>
            {number && (
              <span className={styles.number}>{number.toString()}</span>
            )}

            {category && (
              <CategoryLink
                classNames={[classnames(styles.categoryLink)]}
                category={category.category}
                slug={category.slug}
                onDark={onDark}
              />
            )}
          </div>

          {sponsor && type === ARTICLE_TYPES.collection && (
            <SponsorCard
              classNames={[classnames(styles.sponsorCard)]}
              {...sponsor}
              imagePosition="right"
              onDark={onDark}
            />
          )}

          {episodes && type === ARTICLE_TYPES.collection && (
            <span className={styles.episodes}>
              {episodes}
              <span className={styles.episodesLabel}>{episodesLabel}</span>
            </span>
          )}

          {title && (
            <Heading
              as="h5"
              classNames={[classnames([styles.title, titleClassNames])]}
            >
              {title}
            </Heading>
          )}

          {publishDate && (
            <Date
              {...publishDate}
              classNames={[
                classnames([
                  styles.publishDate,
                  onDark && styles.darkPublishDate,
                ]),
              ]}
              date={publishDate.date}
              format={publishDate.format || "DD MMM 'YY"}
              onDark={onDark}
            />
          )}

          {summary && (
            <Paragraph classNames={[classnames(styles.summary)]}>
              {summary}
            </Paragraph>
          )}

          {ageLimitLabel && ageLimit && (
            <Paragraph classNames={[classnames(styles.ageLimit)]}>
              {ageLimitLabel} {ageLimit}
            </Paragraph>
          )}

          {cta && (
            <div>
              <Button {...cta} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.headerContainer}>
        <div className={styles.imageContainer}>
          {image && <div className={styles.image}>{image}</div>}

          {cardIcon && (
            <CardIcon
              classNames={[classnames(styles.cardIcon, cardIconClassNames)]}
              size="medium"
              theme={onDark ? 'dark' : 'light'}
              {...cardIcon}
            />
          )}

          {imageCount && type === 'Gallery' && (
            <ImageCount
              classNames={[classnames(styles.imageCount)]}
              totalImages={imageCount}
              size={variant === 'mini' ? 'xsmall' : 'medium'}
            />
          )}

          {duration &&
            (type === 'Video' || type === ARTICLE_TYPES.collection) && (
              <Duration
                classNames={[classnames(styles.duration, durationClassNames)]}
                {...duration}
                iconPosition="left"
              />
            )}
        </div>
      </div>
    </div>
  </article>
);

export default ArticleCard;
